import React, { useState, useEffect, useRef } from "react";
import { Button }           from "primereact/button";
import { Dropdown }         from "primereact/dropdown";
import { Toast }            from "primereact/toast";
import { ProgressSpinner }  from "primereact/progressspinner";
import { Menu }             from "primereact/menu";
import { OverlayPanel }     from "primereact/overlaypanel";
import { X }                from "lucide-react";
import { Dialog }           from "primereact/dialog";

import ReusableTable        from "../../dashboard/ReusableTable";
import ReusableForm         from "../../common/ReusableForm";
import UploadTaskForm       from "./UploadTaskForm";
import UpdateTaskPopup      from "./UpdateTaskPopup";
import ReusableDeleteDialog from "../../common/ReusableDeleteDialog";
// import PertConfirmationPopup from "./PertConfirmationPopup";
import {
  getRequest,
  postRequest,
  putRequest,
  deleteRequest
} from "../../services/apiService";

import { useNavigate } from "react-router-dom";

const ActionCell = ({ rowData, onEdit, onDelete }) => {
  const op = useRef(null);

  const menuItems = [
    {
      label: "Edit",
      icon: "pi pi-pencil",
      command: () => {
        op.current.hide();
        onEdit(rowData);
      }
    },
    {
      label: "Delete",
      icon: "pi pi-trash",
      command: () => {
        op.current.hide();
        onDelete(rowData);
      }
    }
  ];

  return (
    <>
      <Button
        icon="pi pi-ellipsis-h"
        className="p-button-rounded p-button-text"
        onClick={(e) => op.current.toggle(e)}
      />
      <OverlayPanel ref={op} className="p-0 shadow-lg">
        <Menu model={menuItems} className="border-none" />
      </OverlayPanel>
    </>
  );
};

/* ---------------------------------------------------------------
 *  Main component
 * ------------------------------------------------------------- */
const TaskTransactionPage = () => {
  const toast = useRef(null);
  const navigate = useNavigate();

  /* ------------------- state ---------------------------------- */
  const [taskTransactions, setTaskTransactions] = useState([]);
  const [loading,           setLoading]         = useState(true);

  /*  modals  */
  const [showFormModal,       setShowFormModal]      = useState(false);
  const [showDeleteModal,     setShowDeleteModal]    = useState(false);
  const [showUploadForm,      setShowUploadForm]     = useState(false);
  const [showUpdateTaskPopup, setShowUpdateTaskPopup]= useState(false);
  const [showPertConfirmation,setShowPertConfirmation]= useState(false);
  const [showTileLayout,      setShowTileLayout]     = useState(false);
  const [showBlockManagement, setShowBlockManagement] = useState(false);
  const [showUnitManagement, setShowUnitManagement] = useState(false);
  const [showEquipmentManagement, setShowEquipmentManagement] = useState(false);
  const [showCompartmentManagement, setShowCompartmentManagement] = useState(false);
  const [showSystemManagement, setShowSystemManagement] = useState(false);

  /*  selections & pagination  */
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [lazyState,           setLazyState]           = useState({
    first: 0,
    rows: 10,
    page: 1
  });
  const [totalRecords,        setTotalRecords] = useState(0);
  const [search,              setSearch]       = useState("");
  const searchTimeout = useRef(null);

  /*  dropdowns  */
  const [projectOptions, setProjectOptions]    = useState([]);
  const [shipOptions,    setShipOptions]       = useState([]);
  const [milestoneOptions,setMilestoneOptions] = useState([]);
  const [taskOptions,    setTaskOptions]       = useState([]);
  const [subtaskOptions, setSubtaskOptions]    = useState([]);
  const [formDialogShipOptions, setFormDialogShipOptions] = useState([]);

  const [dropdownLoading, setDropdownLoading] = useState({
    projects: false, milestones: false, tasks: false,
    ships: false, subtasks: false, formDialogShips: false
  });

  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedShip,    setSelectedShip]    = useState(null);

  /*  formData  */
  const emptyForm = {
    project: "", ship: "", milestone: "", task: "", subtask: "",
    manpower: "", budget: "", contribution_user: "", description: "",
    file: null, taskType: "", taskId: "", unitId: "", subTaskId: "",
    month: "", year: "", userPlanned: "", actualPercent: "",
    actualStart: "", actualEnd: "", start: "", end: ""
  };
  const [formData, setFormData] = useState(emptyForm);

  /*  delete confirm form  */
  const [deleteFormData, setDeleteFormData] = useState({ confirmation: "" });
  const deleteFormFields = [
    {
      name: "confirmation",
      label: "Confirm Deletion",
      type: "text",
      required: true,
      placeholder: "Type 'DELETE' to confirm",
      helpText: "This action cannot be undone."
    }
  ];
  const [taskToDelete, setTaskToDelete] = useState(null);

  /* Add state for PERT confirmation form */
  const [pertConfirmationForm, setPertConfirmationForm] = useState({ pertConfirmation: '' });
  const pertConfirmationFields = [
    {
      name: 'pertConfirmation',
      label: 'Do you have a PERT document?',
      type: 'select',
      options: [
        { label: 'Yes', value: 'yes' },
        { label: 'No', value: 'no' }
      ],
      required: true,
      placeholder: 'Select Yes or No',
      helpText: 'A PERT (Program Evaluation and Review Technique) document is required before proceeding with the upload.'
    }
  ];

  /* -------------------------------------------------------------
   *  ReusableForm field descriptors
   * ----------------------------------------------------------- */
  const formFields = [
    {
      name: "project",
      label: "Project",
      type: "select",
      required: true,
      options: projectOptions,
      placeholder: "Select Project",
      onChange: (e, field, current, setCurrent) => {
        const pid = e.value;
        setCurrent(p => ({ ...p, project: pid, ship: "" }));
        setFormDialogShipOptions([]);
        if (pid) fetchDialogShips(pid);
      }
    },
    {
      name: "ship",
      label: "Ship",
      type: "select",
      required: true,
      options: formDialogShipOptions,
      placeholder: "Select Ship",
      disabled: (d) => !d.project || dropdownLoading.formDialogShips,
      loading: dropdownLoading.formDialogShips,
      onChange: (e, field, current, setCurrent) =>
        setCurrent(p => ({ ...p, ship: e.value }))
    },
    { name: "milestone", label: "Milestone", type: "select", required: true, options: milestoneOptions },
    { name: "task",      label: "Task",      type: "select", required: true, options: taskOptions      },
    { name: "subtask",   label: "Subtask",   type: "select", required: false, options: subtaskOptions  },
    { name: "manpower",  label: "Manpower",  type: "number" },
    { name: "budget",    label: "Budget",    type: "number" },
    { name: "contribution_user", label: "Contribution (%)", type: "number" },
    { name: "description",       label: "Description",      type: "textarea" },
    { name: "file",      label: "File",      type: "file" },
    { name: "taskType",  label: "Task Type", type: "text" },
    { name: "taskId",    label: "Task ID",   type: "text" },
    { name: "unitId",    label: "Unit ID",   type: "text" },
    { name: "subTaskId", label: "Sub Task ID", type: "text" },
    {
      name: "month",
      label: "Month",
      type: "select",
      options: [
        { label: "January",   value: "01" }, { label: "February", value: "02" },
        { label: "March",     value: "03" }, { label: "April",    value: "04" },
        { label: "May",       value: "05" }, { label: "June",     value: "06" },
        { label: "July",      value: "07" }, { label: "August",   value: "08" },
        { label: "September", value: "09" }, { label: "October",  value: "10" },
        { label: "November",  value: "11" }, { label: "December", value: "12" }
      ]
    },
    {
      name: "year",
      label: "Year",
      type: "select",
      options: Array.from({ length: 11 }, (_, i) => ({
        label: String(2020 + i), value: String(2020 + i)
      }))
    },
    { name: "userPlanned",   label: "User Planned %", type: "number" },
    { name: "actualPercent", label: "Actual %",       type: "number" },
    { name: "actualStart",   label: "Actual Start",   type: "date"   },
    { name: "actualEnd",     label: "Actual End",     type: "date"   },
    { name: "start",         label: "Start",          type: "date"   },
    { name: "end",           label: "End",            type: "date"   }
  ];

  /* -------------------------------------------------------------
   *  Fetch helpers
   * ----------------------------------------------------------- */
  const fetchDropdownOptions = async () => {
    setDropdownLoading(d => ({ ...d, projects: true, milestones: true, tasks: true }));
    try {
      const [projRes, msRes, taskRes] = await Promise.all([
        getRequest("/master/projects/"),
        getRequest("/master/milestones/"),
        getRequest("/master/tasks/")
      ]);

      setProjectOptions([
        { label: "All Projects", value: null },
        ...(projRes.results?.data || projRes.data).map(p => ({
          label: p.name, value: p.id
        }))
      ]);

      setMilestoneOptions((msRes.data || msRes.results?.data).map(m => ({
        label: m.title || m.name, value: m.id
      })));

      setTaskOptions((taskRes.data || taskRes.results?.data).map(t => ({
        label: t.name, value: t.id
      })));
      setSubtaskOptions((taskRes.data || taskRes.results?.data).map(t => ({
        label: t.name, value: t.id
      })));
    } catch (err) {
      toast.current?.show({
        severity: "error", summary: "Error", detail: "Dropdown loading failed", life: 3000
      });
    } finally {
      setDropdownLoading(d => ({ ...d, projects: false, milestones: false, tasks: false }));
    }
  };

  const fetchDialogShips = async (projectId) => {
    if (!projectId) return;
    setDropdownLoading(d => ({ ...d, formDialogShips: true }));
    try {
      const res = await getRequest(`/master/projects/?parent_id=${projectId}`);
      const ships = res.data || res.results?.data || [];
      setFormDialogShipOptions(ships.map(s => ({ label: s.name, value: s.id })));
    } catch {
      setFormDialogShipOptions([]);
    } finally {
      setDropdownLoading(d => ({ ...d, formDialogShips: false }));
    }
  };

  const fetchShipsForHeader = async (projectId) => {
    if (!projectId) { setShipOptions([]); return; }
    setDropdownLoading(d => ({ ...d, ships: true }));
    try {
      const res = await getRequest(`/master/projects/?parent_id=${projectId}`);
      const ships = res.data || res.results?.data || [];
      setShipOptions(ships.map(s => ({ label: s.name, value: s.id })));
    } catch {
      setShipOptions([]);
    } finally {
      setDropdownLoading(d => ({ ...d, ships: false }));
    }
  };

  const fetchTaskTransactions = async (page = 1, rows = 10) => {
    setLoading(true);
    try {
      let url = `/transaction/task-transaction?page=${page}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;

      const res = await getRequest(url);
      const list = res.results?.data || [];

      setTaskTransactions(list.map(t => ({
        ...t,
        project_name  : t.project?.name   ?? `Project ${t.project}`,
        task_name     : t.task?.name      ?? "",
        milestone_name: t.milestone?.title?? "",
        budget        : t.budget          ?? 0,
        contribution_user: t.contribution_user ?? 0,
        manpower      : t.manpower        ?? 0
      })));

      setTotalRecords(res.count || 0);
      setLazyState(l => ({ ...l, first: (page-1)*rows, page }));
    } catch (err) {
      toast.current?.show({
        severity: "error", summary: "Error", detail: "Fetch failed", life: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  /* -------------------------------------------------------------
   *  Effects
   * ----------------------------------------------------------- */
  useEffect(() => { fetchDropdownOptions(); fetchTaskTransactions(1, 10); }, []);

  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(
      () => fetchTaskTransactions(lazyState.page, lazyState.rows),
      700
    );
    return () => clearTimeout(searchTimeout.current);
  }, [search, lazyState.page]);

  useEffect(() => {
    if (selectedProject) fetchShipsForHeader(selectedProject);
    else { setShipOptions([]); setSelectedShip(null); }
  }, [selectedProject]);

  /* -------------------------------------------------------------
   *  Handlers
   * ----------------------------------------------------------- */
  const handleProjectChange = (e) => {
    const pid = e.value;
    setSelectedProject(pid);
    setSelectedShip(null);
    setShipOptions([]);
    setSearch("");

    /* keep formData in sync when main header dropdown is used */
    setFormData(fd => ({ ...fd, project: pid, ship: "" }));
  };

  const handleShipChange = (e) => {
    const sid = e.value;
    const lbl = shipOptions.find(o => o.value === sid)?.label || "";
    setSelectedShip(sid);
    setSearch(lbl);
    setFormData(fd => ({ ...fd, ship: sid, milestone:"", task:"", subtask:"" }));
  };

  const handleTableSearch = (v) => setSearch(v);
  const onPageChange       = (evt) => setLazyState(evt);

  /* ---- Add / Edit ------------------------------------------- */
  const openAddDialog = () => {
    setFormData(emptyForm);
    setSelectedTransaction(null);
    setShowFormModal(true);
    setFormDialogShipOptions([]);
  };

  const openEditDialog = async (tr) => {
    setSelectedTransaction(tr);
    const pid  = tr.project?.parent?.id || tr.project?.id;
    const sid  = tr.project?.parent ? tr.project?.id : "";
    const mid  = tr.milestone?.id || "";
    const pTid = tr.task?.parent_task?.id || "";
    const stid = pTid ? tr.task?.id : "";
    const task = pTid ? pTid : tr.task?.id || "";

    setFormData({
      project: pid        || "",
      ship:    sid        || "",
      milestone: mid      || "",
      task:      task     || "",
      subtask:   stid     || "",
      manpower:  tr.manpower            || "",
      budget:    tr.budget              || "",
      contribution_user: tr.contribution_user || "",
      description: tr.description       || "",
      file: null,
      taskType: tr.taskType             || "",
      taskId:   task,
      unitId:   tr.project?.id          || "",
      subTaskId: stid,
      month: tr.month                   || "",
      year:  tr.year                    || "",
      userPlanned:   tr.userPlanned     || "",
      actualPercent: tr.actualPercent   || "",
      actualStart:   tr.actualStart     || "",
      actualEnd:     tr.actualEnd       || "",
      start:         tr.start           || "",
      end:           tr.end             || ""
    });

    if (pid) await fetchDialogShips(pid);
    setShowFormModal(true);
  };

  /* ---- Delete flow ------------------------------------------ */
  const openDeleteModal = (row) => {
    setTaskToDelete(row);
    setDeleteFormData({ confirmation: "" });
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (deleteFormData.confirmation !== "DELETE") {
      toast.current.show({severity:"error", summary:"Error", detail:"Type DELETE to confirm", life:3000});
      return;
    }
    try {
      await deleteRequest(`/transaction/task-transaction/${taskToDelete.id}/`);
      toast.current.show({severity:"success", summary:"Deleted", detail:"Task transaction removed", life:3000});
      if (taskTransactions.length === 1 && lazyState.page > 1)
        setLazyState(l => ({ ...l, page: l.page - 1 }));
      else fetchTaskTransactions(lazyState.page, lazyState.rows);
      setShowDeleteModal(false);
    } catch {
      toast.current.show({severity:"error", summary:"Error", detail:"Delete failed", life:3000});
    }
  };

  /* ---- Save (Add/Edit) -------------------------------------- */
  const handleSaveTransaction = async () => {
    if (!formData.project || !formData.milestone || !formData.task) {
      toast.current.show({severity:"error", summary:"Error", detail:"Required fields missing", life:3000});
      return;
    }
    const payload = {
      project: formData.ship,
      milestone: formData.milestone,
      task: formData.subtask,
      manpower: formData.manpower,
      budget: formData.budget,
      contribution_user: formData.contribution_user,
      description: formData.description,
      file: formData.file,
      taskType: formData.taskType
    };
    try {
      if (selectedTransaction) {
        await putRequest(`/master/task-transactions/${selectedTransaction.id}/`, payload);
        toast.current.show({severity:"success", summary:"Updated", detail:"Task updated", life:3000});
      } else {
        await postRequest("/transaction/task-transaction", payload);
        toast.current.show({severity:"success", summary:"Added", detail:"Task added", life:3000});
      }
      fetchTaskTransactions(lazyState.page, lazyState.rows);
      setShowFormModal(false);
    } catch {
      toast.current.show({severity:"error", summary:"Error", detail:"Save failed", life:3000});
    }
  };

  /* ---- Reset header filters --------------------------------- */
  const handleReset = () => {
    setSelectedProject(null);
    setSelectedShip(null);
    setSearch("");
    setLazyState({ first: 0, rows: 10, page: 1 });
  };

  /* -------------------------------------------------------------
   *  Table column defs
   * ----------------------------------------------------------- */
  const columns = [
    { field: "actions", header: "Actions", body: (row) => (
        <ActionCell rowData={row} onEdit={openEditDialog} onDelete={openDeleteModal} />),
      sortable: false, notFilterable: true
    },
    { field: "project_name", header: "Ship",     sortable: true },
    { field: "task_name",    header: "Task",     sortable: true,
      body: (row) => row.task_name || "" },
    { field: "milestone_name", header: "Milestone", sortable: true },
    { field: "actual_start",   header: "Actual Start", sortable: true,
      body: (row) => row.actual_start ?? "" },
    { field: "actual_finish",  header: "Actual Finish", sortable: true,
      body: (row) => row.actual_finish ?? "" },
    { field: "actual_completion_percent", header: "Actual %", sortable: true,
      body: (row) => `${row.actual_completion_percent ?? 0}%` }
  ];

  /* -------------------------------------------------------------
   *  JSX
   * ----------------------------------------------------------- */
  const handlePertConfirmation = (confirmed) => {
    setShowPertConfirmation(false);
    if (confirmed) {
      setShowUploadForm(true);
    } else {
      setShowTileLayout(true);
    }
  };

  const TileLayoutPopup = () => {
    const tiles = [
      {
        id: 2,
        title: 'Blocks',
        icon: '🏗️',
        description: 'Manage ship blocks and their configurations',
        details: 'View and manage all ship blocks, including their specifications, dimensions, and assembly details.',
        color: 'from-blue-500 to-blue-700',
        iconBg: 'bg-blue-100',
        onClick: () => {
          setShowTileLayout(false);
          setShowBlockManagement(true);
        }
      },
      {
        id: 3,
        title: 'Units',
        icon: '📦',
        description: 'Manage ship units and assemblies',
        details: 'Configure and manage ship units, their components, and assembly relationships.',
        color: 'from-purple-500 to-purple-700',
        iconBg: 'bg-purple-100',
        onClick: () => {
          setShowTileLayout(false);
          setShowUnitManagement(true);
        }
      },
      {
        id: 4,
        title: 'Equipment',
        icon: '⚙️',
        description: 'Manage ship equipment and machinery',
        details: 'Configure and maintain ship equipment, machinery, and their specifications.',
        color: 'from-emerald-500 to-emerald-700',
        iconBg: 'bg-emerald-100',
        onClick: () => {
          setShowTileLayout(false);
          setShowEquipmentManagement(true);
        }
      },
      {
        id: 5,
        title: 'Compartments',
        icon: '🚢',
        description: 'Manage ship compartments and spaces',
        details: 'Configure and manage ship compartments, their dimensions, and relationships.',
        color: 'from-amber-500 to-amber-700',
        iconBg: 'bg-amber-100',
        onClick: () => {
          setShowTileLayout(false);
          setShowCompartmentManagement(true);
        }
      },
      {
        id: 6,
        title: 'Systems',
        icon: '🔧',
        description: 'Manage ship systems and networks',
        details: 'Configure and maintain ship systems, including electrical, mechanical, and control systems.',
        color: 'from-red-500 to-red-700',
        iconBg: 'bg-red-100',
        onClick: () => {
          setShowTileLayout(false);
          setShowSystemManagement(true);
        }
      }
    ];

    return (
      <Dialog
        visible={showTileLayout}
        header="Select Task Transaction Type"
        className="max-w-6xl w-full mx-auto"
        onHide={() => setShowTileLayout(false)}
        modal
        contentClassName="p-0"
      >
        <div className="p-8 bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {tiles.map((tile) => (
              <div
                key={tile.id}
                className="bg-white rounded-2xl shadow-xl border border-gray-200 transition-all duration-300 hover:shadow-2xl cursor-pointer group overflow-hidden flex flex-col h-80"
                onClick={tile.onClick}
                style={{ minHeight: '320px' }}
              >
                <div className={`h-2 w-full bg-gradient-to-r ${tile.color}`}></div>
                <div className="flex-1 flex flex-col items-center justify-center p-8">
                  <div className={`text-5xl mb-5 p-5 rounded-full ${tile.iconBg} flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300`}>
                    {tile.icon}
                  </div>
                  <h2 className="text-2xl font-semibold text-center text-gray-800 mb-2">{tile.title}</h2>
                  <p className="text-gray-600 text-center text-base mb-4">{tile.description}</p>
                  <div className="mt-auto opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium text-white bg-gradient-to-r ${tile.color} shadow-md`}>
                      View Details
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Dialog>
    );
  };

  return (
    <div className="p-4 overflow-x-hidden">
      <Toast ref={toast} position="top-right" />

      {/* Header filters ---------------------------------------- */}
      <div className="flex flex-wrap gap-2 mb-4">
        {/* Project */}
        <div className="flex-1 min-w-[200px] max-w-[300px] relative">
          <Dropdown value={selectedProject} options={projectOptions}
                    onChange={handleProjectChange}
                    placeholder={dropdownLoading.projects?"Loading...":"Select Project"}
                    disabled={dropdownLoading.projects} className="w-full" />
          {dropdownLoading.projects && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <ProgressSpinner style={{ width: 20, height: 20 }} strokeWidth="4" />
            </div>
          )}
        </div>

        {/* Ship */}
        <div className="flex-1 min-w-[200px] max-w-[300px] relative">
          <Dropdown value={selectedShip} options={shipOptions}
                    onChange={handleShipChange}
                    placeholder={dropdownLoading.ships?"Loading...":"Select Ship"}
                    disabled={!selectedProject || dropdownLoading.ships} className="w-full" />
          {dropdownLoading.ships && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <ProgressSpinner style={{ width: 20, height: 20 }} strokeWidth="4" />
            </div>
          )}
        </div>

        {/* Reset & actions */}
        <Button label="Reset" icon="pi pi-refresh" onClick={handleReset}
                className="p-button-secondary border-2 px-3 text-white bg-gradient-to-r from-gray-500 to-gray-600" />
        <Button label="Update Task" icon="pi pi-pencil"
                onClick={() => setShowUpdateTaskPopup(true)}
                className="p-button-info border-2 px-3 text-white bg-gradient-to-r from-green-500 to-green-600" />
        <Button label="Upload Pert" icon="pi pi-upload"
                onClick={() => setShowPertConfirmation(true)}
                className="p-button-success border-2 px-3 text-white bg-gradient-to-r from-blue-500 to-blue-600" />
      </div>

      {/* Tile layout popup ------------------------------------ */}
      <TileLayoutPopup />

      {/* Table ------------------------------------------------ */}
      <div className="overflow-x-auto">
        <ReusableTable
          title={<h2 className="text-xl font-bold">Task Transactions</h2>}
          data={taskTransactions}
          columnFields={columns}
          rows={lazyState.rows}
          loading={loading}
          setAddNewDialogVisible={openAddDialog}
          totalRecords={totalRecords}
          onPageChange={onPageChange}
          first={lazyState.first}
          searchValue={search}
          onSearchChange={handleTableSearch}
        />
      </div>

      {/* Update popup ---------------------------------------- */}
      {showUpdateTaskPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl animate-fade-in flex flex-col">
             <div className="flex items-center justify-between px-6 py-4 bg-[#1e3a8a] text-white rounded-t-xl border-b border-gray-200 sticky top-0 z-10">
              <h3 className="text-xl font-bold tracking-wide">Update Task</h3>
              <button onClick={() => setShowUpdateTaskPopup(false)} className="text-white hover:text-gray-300">
                <X size={24} />
              </button>
            </div>
            <div className="p-4 flex-1 overflow-y-auto">
              <UpdateTaskPopup isOpen onClose={() => setShowUpdateTaskPopup(false)} />
            </div>
          </div>
        </div>
      )}

      {/* Pert confirmation modal */}
      <Dialog
        visible={showPertConfirmation}
        header="PERT Confirmation"
        headerClassName="bg-[#1e3a8a] text-white px-6 py-4 rounded-t-lg"
        className="max-w-md w-full mx-auto"
        onHide={() => setShowPertConfirmation(false)}
        modal
        contentClassName="p-0"
      >
        <ReusableForm
          formData={pertConfirmationForm}
          setFormData={setPertConfirmationForm}
          fields={[
            {
              name: 'pertConfirmation',
              label: 'Do you have a PERT document?',
              type: 'select',
              options: [
                { label: 'Yes', value: 'yes' },
                { label: 'No', value: 'no' }
              ],
              required: true,
              placeholder: 'Select Yes or No',
              helpText: 'A PERT (Program Evaluation and Review Technique) document is required before proceeding with the upload.'
            }
          ]}
          onSubmit={() => {
            handlePertConfirmation(pertConfirmationForm.pertConfirmation === 'yes');
            setPertConfirmationForm({ pertConfirmation: '' });
          }}
          onHide={() => setShowPertConfirmation(false)}
          submitLabel="Continue"
        />
      </Dialog>

      {/* Add / Edit form modal -------------------------------- */}
      {showFormModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl animate-fade-in flex flex-col">
            <ReusableForm
              headerText={selectedTransaction ? "Edit Task Transaction" : "Add Task Transaction"}
              formData={formData}
              setFormData={setFormData}
              fields={formFields}
              onSubmit={handleSaveTransaction}
              onHide={() => setShowFormModal(false)}
              loading={loading}
            />
          </div>
        </div>
      )}

      {/* Delete dialog --------------------------------------- */}
      <ReusableDeleteDialog
        visible={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onDelete={handleDelete}
        fields={deleteFormFields}
        formData={deleteFormData}
        setFormData={setDeleteFormData}
        loading={loading}
        title="Delete Task Transaction"
        description={`Are you sure you want to delete "${taskToDelete?.task_name}"? Type DELETE to confirm.`}
        deleteLabel="Delete"
      />
    </div>
  );
};

export default TaskTransactionPage;
